<template>
  <VModalWidget
    :dismiss="dismiss"
    :submit="doSubmit"
    :submitting="submitting"
    :submitTitle="this.buttonTitle"
    content-class="pt-1"
  >
    <template slot="title">{{ title }}</template>

    <template slot="content">
      <FormErrors :show="showResourceErrors" :errors="resourceErrors" />
      <v-form>
        <v-text-field
          v-if="showDisplay"
          v-model="form.display"
          :label="$t('displayName')"
          :error-messages="displayErrors"
          required
          clearable
          @input="$v.form.display.$touch()"
          @blur="$v.form.display.$touch()"
          v-on:keyup.enter="doSubmit"
        />
        <v-select
          v-if="showOrigin"
          v-model="form.from_state"
          :items="states"
          item-text="display"
          item-value="name"
          :return-object="false"
          :label="$t('origin')"
          :hint="$t('originHint')"
          persistent-hint
          :error-messages="originErrors"
          required
          @input="onStateBlur()"
          @blur="$v.form.from_state.$touch()"
        />
        <v-select
          v-if="showDestination"
          v-model="form.to_state"
          :items="states"
          item-text="display"
          item-value="name"
          :return-object="false"
          :label="$t('destination')"
          :hint="$t('destinationHint')"
          persistent-hint
          :error-messages="destinationErrors"
          required
          @input="onStateBlur()"
          @blur="$v.form.to_state.$touch()"
        />
        <SelectActions
          :flow-id="flowId"
          :selection="form.actions"
          :updateSelection="
            (v) => {
              form.actions = v
            }
          "
        />
      </v-form>
    </template>
  </VModalWidget>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { BaseForm, FormErrors, VModalWidget } from '@argon/app/components'
import { required } from 'vuelidate/lib/validators'
import { NAMESPACE } from '../store'
import SelectActions from './SelectActions'

export default {
  name: 'FlowTransitionCard',
  extends: BaseForm,
  components: {
    VModalWidget,
    FormErrors,
    SelectActions
  },
  props: {
    item: Object,
    origin: String,
    flowId: {
      type: [String, Number],
      required: true
    },
    showDisplay: { type: Boolean, default: false },
    dismiss: { type: Function, required: true },
    ingress: { type: Boolean, default: false }
  },
  validations: {
    form: {
      display: { required },
      to_state: { required },
      from_state: { required }
    }
  },
  data: () => ({
    form: {
      display: '',
      from_state: '',
      to_state: '',
      actions: [],
      guards: []
    },
    localStates: [],
    isEdit: false,
    submitting: false
  }),
  computed: {
    ...mapState(NAMESPACE, ['current']),
    title() {
      return this.isEdit ? this.$t('editTitle') : this.$t('newTitle')
    },
    buttonTitle() {
      return this.isEdit ? this.$t('save') : this.$t('create')
    },
    states() {
      return this.current.meta.states.filter((x) => (this.origin ? x.name !== this.origin : true))
    },
    showOrigin() {
      return this.origin ? this.ingress : true
    },
    showDestination() {
      return this.origin ? !this.ingress : true
    },
    displayErrors() {
      const errors = []
      if (!this.$v.form.display.$dirty) return errors
      !this.$v.form.display.required && errors.push(this.$t('required'))
      this.getServerErrors('display', errors)
      return errors
    },
    destinationErrors() {
      const errors = []
      if (!this.$v.form.to_state.$dirty) return errors
      !this.$v.form.to_state.required && errors.push(this.$t('required'))
      this.getServerErrors('to_state', errors)
      return errors
    },
    originErrors() {
      const errors = []
      if (!this.$v.form.from_state.$dirty) return errors
      !this.$v.form.from_state.required && errors.push(this.$t('required'))
      this.getServerErrors('from_state', errors)
      return errors
    }
  },
  methods: {
    ...mapActions(NAMESPACE, ['createTransition', 'updateTransition', 'deleteTransition']),
    onStateBlur() {
      let from = this.form.from_state ? this.current.meta.states.find((x) => x.name === this.form.from_state) : null
      let to = this.form.to_state ? this.current.meta.states.find((x) => x.name === this.form.to_state) : null
      if (from && to) {
        if (
          !this.form.display ||
          this.form.display.indexOf(from.display) !== -1 ||
          this.form.display.indexOf(to.display) !== -1
        ) {
          this.form.display = `${from.display} ${this.$t('to')} ${to.display}`
        }
      }
    },
    doSubmit() {
      this.resetServerErrors()
      if (this.$v.form.$invalid) {
        return this.$v.form.$touch()
      }
      this.submitting = true
      let callback = this.isEdit ? this.updateTransition : this.createTransition
      let payload = { flowId: this.flowId, data: this.form }
      callback(payload)
        .then((resp) => {
          this.dismiss()
        })
        .catch(this.setServerErrors)
        .finally(() => {
          this.submitting = false
        })
    }
  },
  created() {
    if (this.ingress) {
      this.form.to_state = this.origin
    } else {
      this.form.from_state = this.origin
    }
    if (this.item) {
      this.form = { ...this.item }
      this.isEdit = true
    }
  }
}
</script>

<i18n>
{
  "en": {
    "create": "@:labels.create",
    "destination": "Destination",
    "destinationHint": "Select the destination state for this transition",
    "displayName": "@:labels.displayName",
    "editTitle": "Transition",
    "newTitle": "New Transition",
    "origin": "Origin",
    "originHint": "Select the origin state for this transition",
    "required": "@:labels.required",
    "save": "@:labels.save",
    "to": "to"
  },
  "fr": {
    "create": "@:labels.create",
    "destination": "Destination",
    "destinationHint": "Choisissez l'état de destination pour cette transition",
    "displayName": "@:labels.displayName",
    "editTitle": "Transition",
    "newTitle": "Nouvelle transition",
    "origin": "Origine",
    "originHint": "Choisissez l'état d'origine pour cette transition",
    "required": "@:labels.required",
    "save": "@:labels.save",
    "to": "à"
  }
}
</i18n>
